import * as React from 'react';

import { VEHICLE_LAYOUT } from 'src/features/memberships/types';

type MembershipVehicleLayoutContextType = {
    getVehiclesLayout: (membershipId?: number) => VEHICLE_LAYOUT;
    setVehiclesLayout: (membershipId: number | undefined, layout: VEHICLE_LAYOUT) => void;
};

const MembershipVehicleLayoutContext = React.createContext<MembershipVehicleLayoutContextType | null>(null);

export const useVehiclesLayout = () => {
    const context = React.useContext(MembershipVehicleLayoutContext);
    if (!context) {
        throw new Error('useVehiclesLayout must be used within a MembershipVehicleLayoutContext');
    }
    return context;
};

export const MembershipVehicleLayoutContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [layoutMap, setLayoutMap] = React.useState<Record<number, VEHICLE_LAYOUT>>(() => {
        try {
            const stored = localStorage.getItem('vehicleLayout');
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    });

    const setVehiclesLayout = (membershipId: number | undefined, layout: VEHICLE_LAYOUT) => {
        if (typeof membershipId !== 'number') return;

        setLayoutMap((prev) => {
            const updated = { ...prev, [membershipId]: layout };
            localStorage.setItem('vehicleLayout', JSON.stringify(updated));
            return updated;
        });
    };

    const getVehiclesLayout = (membershipId?: number): VEHICLE_LAYOUT => {
        if (typeof membershipId !== 'number') return VEHICLE_LAYOUT.GRID;
        return layoutMap[membershipId] ?? VEHICLE_LAYOUT.GRID;
    };

    const value = React.useMemo(() => {
        return {
            getVehiclesLayout,
            setVehiclesLayout,
        };
    }, [layoutMap]);

    return <MembershipVehicleLayoutContext.Provider value={value}>{children}</MembershipVehicleLayoutContext.Provider>;
};
